import CustomTooltip from '../../../shared/CustomTooltip';
import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    useCancelRecurringReservationMutation,
    useCancelReservationMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../../shared/AuthRoles';
import toast from '../../../shared/Toast';
import { TooltipCancellationContent } from '../../Policies/PolicyTooltipContent';
import { capitalizeName } from '../../../../shared/utils';
import { bookingPoliciesContent } from '../../Policies/PolicyForm';
import { theme } from '../../../../shared/theme';
import { trackEvent } from '../../../../app/Analytics';

export default function CancelReservationModal(props: any) {
    const {
        onClose,
        open,
        onChange,
        message,
        component,
        id,
        reservationDetails,
        recurringReservations,
    } = props;

    const loggedInUser = UserRole();
    const isMobile = useMediaQuery('(max-width: 770px)');

    const [cancelReservation, { isLoading: isCancellingSingle }] =
        useCancelReservationMutation();
    const [cancelRecurringReservation, { isLoading: isCancellingRecurring }] =
        useCancelRecurringReservationMutation();

    const isRecurring = component === 'recurringReservation';
    const isCancelling = isCancellingSingle || isCancellingRecurring;

    const [cancelReason, setCancelReason] = useState('');
    const [otherReason, setOtherReason] = useState('');
    const [ackSeries, setAckSeries] = useState(false);

    const seriesCount = useMemo(() => {
        if (!isRecurring) return 0;
        if (Array.isArray(recurringReservations))
            return recurringReservations.length;
        if (Array.isArray(reservationDetails)) return reservationDetails.length;
        return 0;
    }, [isRecurring, recurringReservations, reservationDetails]);

    const reasons = useMemo(() => {
        const ownerReasons = [
            { value: "Guest didn't show up", label: "Guest didn't show up" },
            { value: 'Guest Cancellation', label: 'Guest Cancellation' },
            { value: 'Maintenance', label: 'Maintenance' },
            { value: 'Other', label: 'Other' },
            { value: 'Owners Request', label: 'Owners Request' },
            {
                value: 'Property Sold/Unavailable',
                label: 'Property sold/Unavailable',
            },
            {
                value: 'Property Unavailable',
                label: 'Property sold or no longer available for rent',
            },
            { value: 'Safety Concern', label: 'Safety Concern' },
            { value: 'Staff Unavailable', label: 'Staff Unavailable' },
            { value: 'Staffing Issue', label: 'Staffing issue' },
            { value: 'Vehicle Breakdown', label: 'Vehicle breakdown' },
            { value: 'Weather', label: 'Weather' },
        ];

        const guestReasons = [
            {
                value: 'Better Price Elsewhere',
                label: 'Better price elsewhere',
            },
            { value: 'Booked by Mistake', label: 'Booked by mistake' },
            { value: 'Double Booked', label: 'Double booked' },
            { value: 'Event Cancelled', label: 'Event cancelled' },
            {
                value: 'Found Alternative Venue',
                label: 'Found an alternative venue',
            },
            { value: 'Illness or Emergency', label: 'Illness/Emergency' },
            { value: 'No Longer Needed', label: 'No Longer Needed' },
            { value: 'Other', label: 'Other' },
            { value: 'Expensive', label: 'Too Expensive' },
            { value: 'Weather', label: 'Weather' },
            { value: 'Rescheduled', label: 'Work or meeting rescheduled' },
        ];

        return loggedInUser?.role?.includes('owner')
            ? ownerReasons
            : guestReasons;
    }, [loggedInUser?.role]);

    const showPolicy =
        !isRecurring &&
        !!reservationDetails?.approvedAt &&
        reservationDetails?.bookingTotal?.subTotal !== 0;

    const cancellationTypeLabel = capitalizeName(
        !reservationDetails?.policy
            ? 'Fair'
            : reservationDetails?.policy?.cancellationType === 'nonRefundable'
              ? 'Non-Refundable'
              : reservationDetails?.policy?.cancellationType,
    );

    const baseCanSubmit =
        cancelReason !== '' &&
        (cancelReason !== 'Other' || otherReason.trim().length >= 3);

    const canSubmit = baseCanSubmit && (!isRecurring || ackSeries);

    const handleDialogClose = () => {
        if (isCancelling) return;
        onClose();
        trackEvent(
            'Reservations',
            'Cancel Reservation',
            `Cancel Modal Closed (${loggedInUser?.role})`,
        );
    };

    const confirmCancel = async () => {
        const reason =
            cancelReason === 'Other' ? otherReason.trim() : cancelReason;

        try {
            if (isRecurring) {
                const res: any = await cancelRecurringReservation([
                    id,
                    { reason },
                ]);
                if (res?.error) throw res.error;
                toast.success(
                    'Recurring reservation series has been cancelled',
                );
            } else {
                const res: any = await cancelReservation([id, { reason }]);
                if (res?.error) throw res.error;
                toast.success('Reservation has been cancelled');
            }

            setCancelReason('');
            setOtherReason('');
            setAckSeries(false);
            onChange();
        } catch (e) {
            toast.error('Failed to cancel reservation');
        }
    };

    const title = isRecurring
        ? 'Cancel recurring series?'
        : 'Cancel this reservation?';
    const subtitle = message;

    const primaryLabel = isRecurring ? 'Cancel series' : 'Cancel reservation';
    const secondaryLabel = isRecurring ? 'Keep series' : 'Keep reservation';

    return (
        <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth='sm'>
            <DialogTitle sx={{ alignItems: 'flex-start' }}>
                <Stack direction='row' spacing={1.25} alignItems='center'>
                    {isRecurring ? (
                        <WarningAmberRoundedIcon
                            sx={{ color: theme.palette.warning.main }}
                        />
                    ) : null}
                    <Box>
                        {title}
                        <Typography variant='body3' sx={{ opacity: 0.8 }}>
                            {subtitle}
                        </Typography>
                    </Box>
                </Stack>

                <IconButton
                    onClick={handleDialogClose}
                    disabled={isCancelling}
                    size='small'
                    aria-label='close'
                    sx={{ color: 'text.primary' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ py: 2 }}>
                <Stack spacing={2}>
                    {isRecurring ? (
                        <Box
                            sx={{
                                borderRadius: 2,
                                px: 1.5,
                                py: 1.25,
                                border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                backgroundColor:
                                    theme.palette.custom.cardActionBackground,
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.25,
                            }}
                        >
                            <Box
                                sx={{
                                    mt: '2px',
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: theme.palette.warning.main,
                                    flexShrink: 0,
                                }}
                            />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                    variant='body3'
                                    sx={{ fontWeight: 900 }}
                                >
                                    Cancels the whole recurring series
                                    {seriesCount > 0
                                        ? ` (${seriesCount} reservation${seriesCount === 1 ? '' : 's'})`
                                        : ''}
                                </Typography>
                                <Typography
                                    variant='body3'
                                    sx={{
                                        mt: 0.25,
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    Note: To cancel a single date, open that
                                    individual reservation.
                                </Typography>

                                <FormControlLabel
                                    sx={{ mt: 1 }}
                                    control={
                                        <Checkbox
                                            checked={ackSeries}
                                            onChange={(e) =>
                                                setAckSeries(e.target.checked)
                                            }
                                            disabled={isCancelling}
                                        />
                                    }
                                    label={
                                        <Typography variant='body3'>
                                            I understand this cancels the entire
                                            series
                                        </Typography>
                                    }
                                />
                            </Box>
                        </Box>
                    ) : null}

                    {showPolicy ? (
                        <Alert severity='warning' sx={{ borderRadius: 2 }}>
                            <Stack
                                direction='row'
                                spacing={1}
                                alignItems='center'
                            >
                                <Typography
                                    variant='body3'
                                    sx={{ fontWeight: 800 }}
                                >
                                    {cancellationTypeLabel} cancellation policy
                                    will apply.
                                </Typography>
                                <CustomTooltip
                                    title={
                                        <TooltipCancellationContent
                                            content={bookingPoliciesContent}
                                        />
                                    }
                                    placement='right'
                                    arrow
                                >
                                    <span>
                                        <HelpOutlineIcon
                                            sx={{
                                                fontSize: 18,
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </span>
                                </CustomTooltip>
                            </Stack>
                        </Alert>
                    ) : null}

                    <FormControl fullWidth size='small'>
                        <InputLabel
                            id='cancel-reason-label'
                            sx={{
                                ...theme.typography.body1,
                                color: theme.palette.text.secondary,
                                fontSize: '12px',
                                lineHeight: 1.2,
                                transform: 'translate(14px, 14px) scale(1)',
                                '&.MuiInputLabel-shrink': {
                                    transform:
                                        'translate(14px, -9px) scale(0.75)',
                                },
                            }}
                        >
                            Select reason for cancellation
                        </InputLabel>
                        <Select
                            labelId='cancel-reason-label'
                            value={cancelReason}
                            label='Reason for cancellation'
                            onChange={(e) => {
                                setCancelReason(e.target.value);
                                if (e.target.value !== 'Other')
                                    setOtherReason('');
                            }}
                            disabled={isCancelling}
                        >
                            {reasons.map((r) => (
                                <MenuItem key={r.value} value={r.value}>
                                    {r.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {cancelReason === 'Other' ? (
                        <TextField
                            fullWidth
                            value={otherReason}
                            onChange={(e) => setOtherReason(e.target.value)}
                            label='Tell us briefly'
                            placeholder='e.g. Unexpected change of plans'
                            size={isMobile ? 'small' : 'medium'}
                            disabled={isCancelling}
                        />
                    ) : null}

                    {!isRecurring ? (
                        <Typography variant='body3' sx={{ opacity: 0.75 }}>
                            Once cancelled, this can’t be undone.
                        </Typography>
                    ) : null}
                </Stack>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 2.5, py: 2 }}>
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'space-between',
                        flexWrap: isMobile ? 'wrap' : 'nowrap',
                    }}
                >
                    <CustomButton
                        variant='outlined'
                        color='primary'
                        onClick={handleDialogClose}
                        disabled={isCancelling}
                        fullWidth={isMobile}
                    >
                        {secondaryLabel}
                    </CustomButton>

                    <CustomButton
                        loading={isCancelling}
                        variant='contained'
                        onClick={confirmCancel}
                        disabled={!canSubmit}
                        fullWidth={isMobile}
                        color='warning'
                    >
                        {primaryLabel}
                    </CustomButton>
                </Box>
            </DialogActions>
        </Dialog>
    );
}
